/**
 * SaveToFolderToast — soft reminder that appears after a user finalizes
 * or emails a meeting, nudging them to download the exports and save
 * them to the project management folder. Bottom-right toast, slides
 * in, auto-dismisses after 8 seconds. Includes quick-action .docx and
 * .pdf download buttons so the user can act on the reminder without
 * having to find the main Export button.
 *
 * Fire-once-per-mount semantics live on the parent (EditorRoute) via
 * a ref — email auto-finalize shouldn't produce two toasts back-to-back.
 */
import { useEffect, useState } from 'react';

interface Props {
  isOpen: boolean;
  /** Whether a download is currently in flight (disables both buttons). */
  isDownloading?: boolean;
  onDownloadDocx: () => void;
  onDownloadPdf: () => void;
  onDismiss: () => void;
  /** How long until the toast auto-dismisses, ms. Default 8000. */
  autoDismissMs?: number;
}

export function SaveToFolderToast({
  isOpen,
  isDownloading = false,
  onDownloadDocx,
  onDownloadPdf,
  onDismiss,
  autoDismissMs = 8000,
}: Props) {
  // Tracks the rendered state separately so we can play the exit
  // animation before unmounting. `visible` drives the transform/opacity
  // classes; `mounted` decides whether the DOM node exists at all.
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      // Next tick so the entrance transition has a from-state to
      // animate from. Without the rAF, the node mounts already at
      // its "visible" position and no slide plays.
      const raf = requestAnimationFrame(() => setVisible(true));

      // Auto-dismiss timer — starts when the toast opens.
      const timer = setTimeout(() => onDismiss(), autoDismissMs);

      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(timer);
      };
    } else if (mounted) {
      // Play exit animation, then unmount after it finishes.
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 220);
      return () => clearTimeout(timer);
    }
    // Explicit no-op return for the !isOpen && !mounted path so
    // every code path in this effect returns the same shape (undef
    // = no cleanup). Silences TS7030.
    return undefined;
  }, [isOpen, mounted, onDismiss, autoDismissMs]);

  if (!mounted) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={
        'fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] ' +
        'rounded-lg border border-pine bg-pine p-4 text-cloud shadow-lg ' +
        'transition-all duration-200 ' +
        (visible
          ? 'translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-4 opacity-0')
      }
      style={{ boxShadow: '0 12px 32px rgba(43, 42, 47, 0.28)' }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cloud/80">
            Reminder
          </div>
          <p className="mt-1 text-sm font-medium text-cloud">
            Save a copy to the project management folder.
          </p>
          <p className="mt-0.5 text-xs text-cloud/75">
            Download and drop the file into the project&rsquo;s folder so
            the team can find it alongside the other minutes.
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="-mr-1 -mt-1 rounded p-1 text-cloud/70 transition-colors hover:text-cloud"
          aria-label="Dismiss reminder"
        >
          ✕
        </button>
      </div>

      <div className="mt-3 flex gap-2">
        {/* White-pill download buttons, pine text. Mirrors the rest of
            the app's "solid accent + white content" treatment but
            inverted because the surface is already pine. */}
        <button
          type="button"
          onClick={onDownloadDocx}
          disabled={isDownloading}
          className="flex-1 rounded-full bg-cloud px-3 py-1.5 text-xs font-medium text-pine transition-colors hover:bg-cloud/90 disabled:opacity-60"
        >
          Download .docx
        </button>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={isDownloading}
          className="flex-1 rounded-full bg-cloud px-3 py-1.5 text-xs font-medium text-pine transition-colors hover:bg-cloud/90 disabled:opacity-60"
        >
          Download .pdf
        </button>
      </div>
    </div>
  );
}
